import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';
import { compose } from 'redux';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import DateTimePicker from '../../../core/components/DateTimePicker';
import { selectInventoryTypes } from '../reducer';
import { selectEmployees } from '../../employees/reducer';
import { fetchEmployees } from '../../employees/actions';
import {
  fieldRequired,
  requiredInput,
  requiredSelect,
  Select,
  TextArea,
} from '../../../core/formValidation';
import UserGroupPermissions, {
  viewPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { InventoryType } from '../models';

const InventoryEditForm = forwardRef(
  ({ intl, inventoryType, initialValues, isNew, isEssentialPart, onSubmit }, ref) => {
    const methods = useForm({ defaultValues: initialValues || {} });
    const { control, handleSubmit, setValue } = methods;
    const [type, setType] = useState(inventoryType);
    const [essential, setEssential] = useState(
      isEssentialPart || initialValues.priority ? true : false
    );

    const dispatch = useDispatch();
    const inventoryTypes = useSelector(selectInventoryTypes);
    const employees = useSelector(selectEmployees);

    useEffect(() => {
      setType(inventoryType);
      // Ensure the RHF field reflects a valid default type without requiring user interaction.
      // Prefer an existing value; otherwise map from the provided inventoryType enum to a matching inventoryTypes entry.
      const currentTypeId = methods.getValues ? methods.getValues('inventoryTypeId') : undefined;
      if (currentTypeId == null || currentTypeId === '') {
        let defaultTypeId;
        // Try to find an inventory type whose "type" matches the provided enum
        const matched =
          inventoryTypes &&
          inventoryTypes.find &&
          inventoryTypes.find((t) => t?.type === inventoryType);
        if (matched && matched.id != null) {
          defaultTypeId = matched.id;
        } else if (inventoryTypes && inventoryTypes.length > 0) {
          // Fallback to the first available inventory type
          defaultTypeId = inventoryTypes[0].id;
        }
        if (defaultTypeId != null) {
          methods.setValue('inventoryTypeId', defaultTypeId, { shouldValidate: true });
        }
      }
    }, [inventoryType, inventoryTypes]);

    useEffect(() => {
      dispatch(fetchEmployees());
    }, []);

    useImperativeHandle(ref, () => ({
      submit: () => handleSubmit((vals) => onSubmit && onSubmit(vals))(),
    }));

    const createInventoryTypeOptions = () => {
      return inventoryTypes.map((typeOption) => ({ value: typeOption.id, text: typeOption.name }));
    };

    return (
      <FormProvider {...methods}>
        <p className={`form-alert notice${!essential ? ' hidden' : ''}`}>
          <i className="uil uil-info-circle"></i>
          {isNew
            ? intl.formatMessage({ id: 'essentialInventoryCreateWarning' })
            : intl.formatMessage({ id: 'essentialInventoryUpdateWarning' })}
        </p>
        {isNew && (
          <UserGroupPermissions itemRef={viewPermissionRefs.essential_parts}>
            <div className="form-row">
              <div className="form-check">
                <label className="form-check-labels">
                  <Controller
                    name="priority"
                    render={({ field: { value = essential, onChange, ...rest } }) => (
                      <input
                        id="priority"
                        className="form-check-input"
                        type="checkbox"
                        checked={!!value}
                        onChange={(e) => {
                          setEssential(e.target.checked);
                          onChange(e.target.checked);
                        }}
                        {...rest}
                      />
                    )}
                  />
                  {intl.formatMessage({ id: 'priority' })}
                </label>
              </div>
            </div>
          </UserGroupPermissions>
        )}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="type">{intl.formatMessage({ id: 'type' })}</label>
            <Controller
              name="inventoryTypeId"
              render={({ field: { value, onChange } }) =>
                requiredSelect({
                  input: {
                    value,
                    onChange: (e) => {
                      const v = Number(e?.target?.value ?? e);
                      setType(v);
                      onChange(v);
                    },
                  },
                  id: 'inventoryTypeId',
                  className: 'form-control',
                  options: createInventoryTypeOptions(),
                })
              }
            />
          </div>
          <div className="form-group">
            <label htmlFor="name">{intl.formatMessage({ id: 'name' })}</label>
            <Controller
              name="name"
              render={({ field }) =>
                requiredInput({
                  input: field,
                  id: 'name',
                  type: 'text',
                  className: 'form-control',
                  placeholder: intl.formatMessage({ id: 'enterPartName' }),
                })
              }
            />
          </div>
          <div className="form-group">
            <label htmlFor="number">{intl.formatMessage({ id: 'partNumber' })}</label>
            <Controller
              name="number"
              render={({ field }) =>
                requiredInput({
                  input: field,
                  id: 'number',
                  type: 'text',
                  className: 'form-control',
                  placeholder: intl.formatMessage({ id: 'enterPartNumber' }),
                })
              }
            />
          </div>
        </div>
        <div className="form-row full">
          <div className="form-group">
            <label htmlFor="description">{intl.formatMessage({ id: 'description' })}</label>
            <Controller
              name="description"
              render={({ field }) => (
                <TextArea
                  input={field}
                  id="description"
                  className="form-control"
                  maxLength={400}
                  placeholder={intl.formatMessage({ id: 'enterItemDescription' })}
                />
              )}
            />
          </div>
        </div>
        {!essential && (
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">{intl.formatMessage({ id: 'installedBy' })}</label>
              <Controller
                name="employeeId"
                render={({ field: { value, onChange } }) => (
                  <Select
                    input={{ value, onChange: (e) => onChange(Number(e?.target?.value ?? e)) }}
                    id="employeeId"
                    className="form-control"
                    options={[
                      { value: 0, text: intl.formatMessage({ id: 'selectEmployee' }) },
                      ...employees.map((e) => ({
                        value: e.id,
                        text: `${e.lastName}, ${e.firstName}`,
                      })),
                    ]}
                  />
                )}
              />
            </div>
            {type !== InventoryType.Detergent && (
              <React.Fragment>
                <div className="form-group d-flex flex-column">
                  <label htmlFor="installedAt" className="mr-2">
                    {intl.formatMessage({ id: 'installedAt' })}
                  </label>
                  <Controller
                    name="installedAt"
                    render={({ field }) => (
                      <DateTimePicker
                        input={field}
                        id="installedAt"
                        className="form-control"
                        placeholderText={intl.formatMessage({ id: 'selectInstallationDate' })}
                      />
                    )}
                  />
                </div>
              </React.Fragment>
            )}
          </div>
        )}
        <div className="form-row">
          {!essential && (
            <div className="form-group">
              <label htmlFor="onHand">{intl.formatMessage({ id: 'stockOnHand' })}</label>
              <Controller
                name="onHand"
                render={({ field: { value, onChange } }) => (
                  <input
                    id="onHand"
                    type="number"
                    className="form-control"
                    value={value || ''}
                    onChange={(e) => onChange(Number(e.target.value))}
                    placeholder={intl.formatMessage({ id: 'enterStockOnHand' })}
                  />
                )}
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="preferredStockLevel">
              {intl.formatMessage({ id: 'preferredStockLevel' })}
            </label>
            <Controller
              name="preferredStockLevel"
              render={({ field: { value, onChange } }) => (
                <input
                  id="preferredStockLevel"
                  min="1"
                  step="1"
                  type="number"
                  className="form-control"
                  value={value || ''}
                  onChange={(e) => onChange(Number(e.target.value))}
                  placeholder={intl.formatMessage({ id: 'enterPreferredStockLevel' })}
                />
              )}
            />
          </div>
          <div className="form-group">
            <label htmlFor="unitOfMeasure">{intl.formatMessage({ id: 'units' })}</label>
            <Controller
              name="unitOfMeasure"
              render={({ field: { value, onChange } }) => (
                <Select
                  input={{ value, onChange }}
                  id="unitOfMeasure"
                  options={[
                    { value: 'Number', text: intl.formatMessage({ id: 'number' }) },
                    { value: 'Gallons', text: intl.formatMessage({ id: 'gallons' }) },
                    { value: 'Feet', text: intl.formatMessage({ id: 'feet' }) },
                  ]}
                />
              )}
            />
          </div>
        </div>
        <div className="form-row full">
          <div className="form-group">
            <label htmlFor="preferredStockLevel">{intl.formatMessage({ id: 'url' })}</label>
            <Controller
              name="vendorUrl"
              render={({ field }) => (
                <input
                  {...field}
                  id="vendorUrl"
                  type="string"
                  className="form-control"
                  placeholder={intl.formatMessage({ id: 'enterVendorLink' })}
                />
              )}
            />
          </div>
        </div>
      </FormProvider>
    );
  }
);

InventoryEditForm.propTypes = {
  intl: PropTypes.shape({}).isRequired,
  inventoryType: PropTypes.number,
  initialValues: PropTypes.shape({}),
  isNew: PropTypes.bool,
  isEssentialPart: PropTypes.bool,
  onSubmit: PropTypes.func,
};

const ConnectedInventoryForm = connect(null, null, null, { forwardRef: true })(InventoryEditForm);

export default injectIntl(ConnectedInventoryForm, { forwardRef: true });
